import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  
} from '@mui/material';
import EventCalendar from '../../components/Calendar/EventCalendar';
import VideoCallOutlinedIcon from '@mui/icons-material/VideoCallOutlined';
import PageLayout from '../../components/layout/PageLayout';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { extractList } from '../../utils/response';
import { getMeetings, updateMeetingStatus } from '../../store/slices/meetingsSlice';
import { getLeadsStatus, updateLeadStatus } from '../../store/slices/leadsSlice';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { MEETING_VIEW_PATH, MEETING_CREATE_PATH, MEETING_EDIT_PATH } from '../../navigations/path';
import { Visibility as ViewIcon, Add as AddIcon, Edit as EditIcon, MoreVert as MoreVertIcon, SwapHoriz as ChangeStatusIcon } from '@mui/icons-material';
import Can from '../../components/permissions/Can';

// Memoized table row with dropdown actions
const MeetingTableRow = React.memo(({ meeting, onEdit, onView, onEditMeeting, getStatusColor, formatDateTime, canView, canEdit, canChangeStatus }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const googleLink = meeting?.google_event_url || meeting?.google_meet_link || meeting?.google_meet_url || '';

  

  const handleOpenMenu = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleCloseMenu = () => setAnchorEl(null);

  const handleView = () => {
    handleCloseMenu();
    onView(meeting);
  };
  const handleEditMeeting = () => {
    handleCloseMenu();
    onEditMeeting(meeting);
  };
  const handleChangeStatus = () => {
    handleCloseMenu();
    onEdit(meeting);
  };

  return (
    <TableRow key={meeting.id} hover>
      <TableCell>
        <Chip
          label={meeting.status?.name || 'Meeting'}
          color={getStatusColor(meeting.status?.name)}
          size="small"
        />
      </TableCell>
      <TableCell>{meeting.time_zone}</TableCell>
      <TableCell>{formatDateTime(meeting.start_date_time)}</TableCell>
      <TableCell>{formatDateTime(meeting.end_date_time)}</TableCell>
      <TableCell>
        <Box>
          <Typography variant="subtitle2" fontWeight={500}>
            {meeting.lead?.name || 'Unknown Lead'}
          </Typography>
          {meeting.lead?.email && (
            <Typography variant="body2" color="text.secondary">
              {meeting.lead.email}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={meeting.description}
        >
          {meeting.description || 'No description'}
        </Typography>
      </TableCell>
      {/* <TableCell>
        {googleLink ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                maxWidth: 160,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={googleLink}
            >
              {googleLink}
            </Typography>
            <Tooltip title="Copy link">
              <IconButton size="small" onClick={handleCopyLink} aria-label="Copy Google Meet link">
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )}
      </TableCell> */}
      <TableCell sx={{ textAlign: 'center' }}>
        <IconButton
          size="small"
          onClick={handleOpenMenu}
          aria-controls={open ? `meeting-menu-${meeting.id}` : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          title="Actions"
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id={`meeting-menu-${meeting.id}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          MenuListProps={{ 'aria-labelledby': `meeting-menu-${meeting.id}` }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {canView && (
            <MenuItem onClick={handleView}>
              <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
              <ListItemText>View</ListItemText>
            </MenuItem>
          )}
          {googleLink && (
            <MenuItem onClick={()=>{window.open(googleLink, '_blank', 'noopener,noreferrer');}}>
              <ListItemIcon><VideoCallOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Open Meeting</ListItemText>
            </MenuItem>
          )}
          {canEdit && (
            <MenuItem onClick={handleEditMeeting}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit Meeting</ListItemText>
            </MenuItem>
          )}
          {canChangeStatus && (
            <MenuItem onClick={handleChangeStatus}>
              <ListItemIcon><ChangeStatusIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Change Status</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </TableCell>
    </TableRow>
  );
});

const MeetingCalendar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canView = hasPermission('meeting-view');
  const canEdit = hasPermission('meeting-edit');
  const canChangeStatus = hasPermission('meeting-status');
  
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusOptions, setStatusOptions] = useState([]);
  const [leadStatusOptions, setLeadStatusOptions] = useState([]);
  // const [frontSellerOptions, setFrontsellerOptions] = useState([]);

  // Change Status Dialog State
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusDescription, setStatusDescription] = useState('');
  const [processing, setProcessing] = useState(false);

  // Reschedule Dialog State
  const [openRescheduleDialog, setOpenRescheduleDialog] = useState(false);
  const [rescheduleMeeting, setRescheduleMeeting] = useState(null); // Store meeting for reschedule separately
  const [rescheduleData, setRescheduleData] = useState({
    timeZone: 'UTC',
    startDateTime: '',
    endDateTime: '',
    meetingDescription: '',
    assignee_id: user?.id || '',
  });
  const [errors, setErrors] = useState({});

  // Memoized functions
  const getStatusColor = useCallback((statusName) => {
    const colorMap = {
      'Upcoming': 'primary',
      'Completed': 'success',
      'Cancelled': 'error',
      'Rescheduled': 'warning',
      'In Progress': 'info',
      'Meeting': 'primary',
    };
    return colorMap[statusName] || 'default';
  }, []);

  const formatDateTime = useCallback((dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    try {
      const date = new Date(dateTimeString.replace(' ', 'T') + 'Z');
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  }, []);

  // const formatDateTimeForInput = useCallback((dateTimeString) => {
  //   if (!dateTimeString) return '';
  //   try {
  //     const date = new Date(dateTimeString.replace(' ', 'T') + 'Z');
  //     return date.toISOString().slice(0, 16);
  //   } catch (error) {
  //     return '';
  //   }
  // }, []);

  // API calls
  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await dispatch(getMeetings({
        'relations[0]': 'status',
        'relations[1]': 'lead',
      })).unwrap();
      const data = res?.data?.list ?? [];
      setMeetings(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
      toast.error('Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const fetchMetaData = useCallback(async () => {
    try {
      const [statusRes, leadStatusRes] = await Promise.all([
        dispatch(getLeadsStatus({ 'conditions[model]': 'Meeting' })).unwrap(),
        dispatch(getLeadsStatus({ 'conditions[model]': 'Lead' })).unwrap(),
      ]);
      const statusList = extractList(statusRes);
      setStatusOptions(Array.isArray(statusList) ? statusList : []);
      const leadList = extractList(leadStatusRes);
      setLeadStatusOptions(Array.isArray(leadList) ? leadList : []);
      return {
        statusOptions: Array.isArray(statusList) ? statusList : [],
        leadStatusOptions: Array.isArray(leadList) ? leadList : [],
      };
    } catch (error) {
      console.error('Failed to load metadata:', error);
      toast.error('Failed to load metadata');
      return { statusOptions: [], leadStatusOptions: [] };
    }
  }, [dispatch]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const handleViewMeeting = useCallback((meeting) => {
    if (meeting?.id) navigate(`${MEETING_VIEW_PATH}/${meeting.id}`);
  }, [navigate]);

  const handleEditMeeting = useCallback((meeting) => {
    if (meeting?.id) navigate(`${MEETING_EDIT_PATH}/${meeting.id}`);
  }, [navigate]);

  const handleOpenStatusDialog = useCallback(async (meeting) => {
    setSelectedMeeting(meeting);
    setNewStatus(meeting.status?.id || '');
    setStatusDescription(meeting.description || '');
    await fetchMetaData();
    setOpenStatusDialog(true);
  }, [fetchMetaData]);

  const handleCloseStatusDialog = useCallback(() => {
    setOpenStatusDialog(false);
    setSelectedMeeting(null);
    setNewStatus('');
    setStatusDescription('');
    setProcessing(false);
  }, []);

  const handleCloseRescheduleDialog = useCallback(() => {
    setOpenRescheduleDialog(false);
    setRescheduleMeeting(null);
    setRescheduleData({
      timeZone: 'UTC',
      startDateTime: '',
      endDateTime: '',
      meetingDescription: '',
      assignee_id: user?.id || '',
    });
    setErrors({});
    setProcessing(false);
  }, []);

  const handleStatusChange = useCallback(async () => {
    if (!newStatus || !selectedMeeting) {
      toast.warn('Please select a status');
      return;
    }

    const selectedStatus = statusOptions.find(s => s.id === newStatus);
    try {
      setProcessing(true);
      const payload = {
        status_id: newStatus,
        ...(statusDescription.trim() && { description: statusDescription.trim() }),
      };

      const res = await dispatch(updateMeetingStatus({ meetingId: selectedMeeting.id, payload })).unwrap();
      if (res?.success) {
        toast.success('Meeting status updated successfully');
        fetchMeetings();
        handleCloseStatusDialog();

        if (selectedStatus?.name === 'Rescheduled') {
          setRescheduleMeeting(selectedMeeting);
          setRescheduleData({
            timeZone: selectedMeeting.time_zone || 'UTC',
            startDateTime: '',
            endDateTime: '',
            meetingDescription: `Rescheduled: ${selectedMeeting.description || ''}`,
            assignee_id: user?.id || '',
          });
          setOpenRescheduleDialog(true);
        }
      }
    } catch (error) {
      console.error('Failed to update meeting status:', error);
      toast.error(error.message || 'Failed to update meeting status');
    } finally {
      setProcessing(false);
    }
  }, [newStatus, statusDescription, statusOptions, selectedMeeting, fetchMeetings, handleCloseStatusDialog, user?.id]);

  const handleRescheduleSubmit = useCallback(async () => {
    // Validation
    const newErrors = {};
    if (!rescheduleData.startDateTime) newErrors.startDateTime = 'Start date time is required';
    if (!rescheduleData.endDateTime) newErrors.endDateTime = 'End date time is required';
    if (!rescheduleData.meetingDescription) newErrors.meetingDescription = 'Description is required';
    // if (!rescheduleData.assignee_id) newErrors.assignee_id = 'Assignee is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Check if rescheduleMeeting is available
    if (!rescheduleMeeting || !rescheduleMeeting.lead) {
      toast.error('Original meeting data not found. Please try again.');
      handleCloseRescheduleDialog();
      return;
    }

    try {
      setProcessing(true);

      // Find the "Meeting" status ID from lead status options
      let statuses = leadStatusOptions;
      if (!statuses.length) {
        const meta = await fetchMetaData();
        statuses = meta.leadStatusOptions || [];
      }
      const meetingStatus = statuses.find(s => s.name === 'Meeting');
      if (!meetingStatus) {
        toast.error('Meeting status not found in lead status options');
        return;
      }

      // Update lead status with rescheduled meeting details
      const payload = {
        status_id: meetingStatus.id,
        time_zone: rescheduleData.timeZone,
        start_date_time: rescheduleData.startDateTime,
        end_date_time: rescheduleData.endDateTime,
        description: rescheduleData.meetingDescription,
        assignee_id: rescheduleData.assignee_id,
      };

      // Use the lead ID from the reschedule meeting
      const leadId = rescheduleMeeting.lead.id;
      if (!leadId) {
        toast.error('Lead ID not found');
        return;
      }

      const res = await dispatch(updateLeadStatus({
        leadId,
        payload,
      })).unwrap();
      if (res?.success) {
        toast.success('Meeting rescheduled successfully');
        // Refresh meetings to show the updated meeting
        fetchMeetings();
        handleCloseRescheduleDialog();
      }
    } catch (error) {
      console.error('Failed to reschedule meeting:', error);
      toast.error(error.message || 'Failed to reschedule meeting');
    } finally {
      setProcessing(false);
    }
  }, [rescheduleData, rescheduleMeeting, leadStatusOptions, fetchMeetings, handleCloseRescheduleDialog, fetchMetaData]);

  // const getSelectedAssignee = useCallback(() => {
  //   return frontSellerOptions.find(seller => seller.id === rescheduleData.assignee_id) || null;
  // }, [frontSellerOptions, rescheduleData.assignee_id]);

  // const handleAssignToChange = useCallback((event, value) => {
  //   setRescheduleData(prev => ({ ...prev, assignee_id: value ? value.id : '' }));
  //   if (errors.assignee_id) {
  //     setErrors(prev => ({ ...prev, assignee_id: '' }));
  //   }
  // }, [errors.assignee_id]);

  const handleRescheduleChange = useCallback((field, value) => {
    setRescheduleData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  // Memoized table data
  const meetingTableRows = useMemo(() => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    if (meetings.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              No meetings found
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return meetings.map((meeting) => (
      <MeetingTableRow
        key={meeting.id}
        meeting={meeting}
        onEdit={handleOpenStatusDialog}
        onView={handleViewMeeting}
        onEditMeeting={handleEditMeeting}
        getStatusColor={getStatusColor}
        formatDateTime={formatDateTime}
        canView={canView}
        canEdit={canEdit}
        canChangeStatus={canChangeStatus}
      />
    ));
  }, [meetings, loading, handleOpenStatusDialog, handleViewMeeting, handleEditMeeting, getStatusColor, formatDateTime]);

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Calendar Component */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <EventCalendar meetings={meetings} loading={loading} />
            </CardContent>
          </Card>

          {/* Meetings Table */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Meetings List ({meetings.length})
                </Typography>
                <Can permission="meeting-create">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(MEETING_CREATE_PATH)}
                  >
                    Create Meeting
                  </Button>
                </Can>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Meeting Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Time Zone</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Lead Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      {/* <TableCell sx={{ fontWeight: 600 }}>Google Meet Link</TableCell> */}
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {meetingTableRows}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
      </Container>

      {/* Change Status Dialog */}
      <Dialog open={openStatusDialog} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Change Meeting Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="New Status"
            >
              {statusOptions.map((status) => (
                <MenuItem key={status.id} value={status.id}>{status.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Description"
            multiline
            rows={3}
            value={statusDescription}
            onChange={(e) => setStatusDescription(e.target.value)}
            placeholder="Optional note or reason for status change…"
            fullWidth
            sx={{ mt: 2 }}
          />

          {(() => {
            const selectedStatus = statusOptions.find((s) => s.id === newStatus);
            const statusName = selectedStatus?.name;

            if (statusName === 'Rescheduled') {
              return (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                    This will mark the current meeting as Rescheduled and create a new meeting with updated details for the lead.
                  </Typography>
                </Box>
              );
            }

            return null;
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancel</Button>
          <Button
            onClick={handleStatusChange}
            variant="contained"
            disabled={!newStatus || processing}
            startIcon={processing ? <CircularProgress size={20} /> : null}
          >
            {processing ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={openRescheduleDialog} onClose={handleCloseRescheduleDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Reschedule Meeting
          {rescheduleMeeting && (
            <Typography variant="body2" color="text.secondary">
              Create new meeting for lead: {rescheduleMeeting.lead?.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, gap: 2, display: 'flex', flexDirection: 'column' }}>
            {/* <FormControl fullWidth>
              <Autocomplete
                options={frontSellerOptions}
                getOptionLabel={(option) => option.name}
                value={getSelectedAssignee()}
                onChange={handleAssignToChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select assignee"
                    error={!!errors.assignee_id}
                    helperText={errors.assignee_id}
                    label="Select Assignee"
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No assignee found"
              />
            </FormControl> */}

            <FormControl fullWidth>
              <InputLabel>Time Zone</InputLabel>
              <Select
                value={rescheduleData.timeZone}
                onChange={(e) => handleRescheduleChange('timeZone', e.target.value)}
                label="Time Zone"
              >
                <MenuItem value="UTC">UTC</MenuItem>
                <MenuItem value="EST">EST</MenuItem>
                <MenuItem value="PST">PST</MenuItem>
                <MenuItem value="CST">CST</MenuItem>
                <MenuItem value="GMT">GMT</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="New Start Date & Time"
              type="datetime-local"
              value={rescheduleData.startDateTime}
              onChange={(e) => handleRescheduleChange('startDateTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!errors.startDateTime}
              helperText={errors.startDateTime}
              fullWidth
              required
            />

            <TextField
              label="New End Date & Time"
              type="datetime-local"
              value={rescheduleData.endDateTime}
              onChange={(e) => handleRescheduleChange('endDateTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!errors.endDateTime}
              helperText={errors.endDateTime}
              fullWidth
              required
            />

            <TextField
              label="Meeting Description"
              multiline
              rows={3}
              value={rescheduleData.meetingDescription}
              onChange={(e) => handleRescheduleChange('meetingDescription', e.target.value)}
              placeholder="Agenda, purpose, reason for rescheduling…"
              error={!!errors.meetingDescription}
              helperText={errors.meetingDescription}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRescheduleDialog}>Cancel</Button>
          <Button
            onClick={handleRescheduleSubmit}
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : null}
          >
            {processing ? 'Creating...' : 'Create Rescheduled Meeting'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default MeetingCalendar;